'use client';

import { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  ThumbsUp,
  ThumbsDown,
  Reply,
  MoreHorizontal,
  Heart,
  LinkIcon,
  Flag,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useComments } from '../../hooks/use-comments';
import { useGetUserById } from '../../hooks/use-user';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { CheckCircle } from 'lucide-react';
import { useAuthStore } from '../../stores';
import { ReportDialog } from '@/components/modals/report-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function ForumCommentItem({
  comment,
  postId,
  currentUser,
  isReply = false,
}) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [mintDialogOpen, setMintDialogOpen] = useState(false);
  const { addComment, likeInteraction, dislikeInteraction } = useComments(
    postId,
    'discussion'
  );

  // Get wallet address from Passport SSO auth store (replaces useActiveAccount)
  const { walletAddress } = useAuthStore();

  // Fetch the comment creator's profile
  const {
    data: author,
    isLoading: isLoadingCreator,
    error: creatorError,
  } = useGetUserById(comment?.creator);

  // Get like/dislike mutations
  const likeMutation = likeInteraction();
  const dislikeMutation = dislikeInteraction();

  // Handle like
  const handleLike = async () => {
    if (!currentUser?.oid) return;
    try {
      await likeMutation.mutateAsync({
        userAddress: walletAddress,
        commentOid: comment.oid,
        uOID: currentUser.oid,
      });
    } catch (error) {
      console.error('Error liking comment:', error);
    }
  };

  // Handle dislike
  const handleDislike = async () => {
    if (!currentUser?.oid) return;
    try {
      await dislikeMutation.mutateAsync({
        userAddress: walletAddress,
        commentOid: comment.oid,
        uOID: currentUser.oid,
      });
    } catch (error) {
      console.error('Error disliking comment:', error);
    }
  };

  // Format the date
  const formattedDate = comment.createdAt
    ? formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })
    : 'recently';

  // Handle reply submission
  const handleReplySubmit = async () => {
    if (!replyContent.trim()) return;

    try {
      await addComment().mutateAsync({
        postId,
        content: replyContent,
        authorId: currentUser?.id,
        parentId: comment.id,
      });

      setReplyContent('');
      setShowReplyForm(false);
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  };

  return (
    <div className="p-6" data-comment-oid={comment?.oid}>
      {/* Header - Same as discussion */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarImage
              src={author?.avatar || '/placeholder.svg'}
              alt={author?.username || 'User'}
            />
            <AvatarFallback>
              {author?.username?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">
                {author?.username || 'Anonymous User'}
              </span>

              {/* Mint Information Badge */}
              {comment.mintInformation && (
                <Dialog open={mintDialogOpen} onOpenChange={setMintDialogOpen}>
                  <DialogTrigger asChild>
                    <Badge
                      variant="outline"
                      className="cursor-pointer flex items-center gap-1 bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20 transition-colors"
                      onClick={(e) => {
                        e.preventDefault();
                        setMintDialogOpen(true);
                      }}
                    >
                      <CheckCircle className="h-3 w-3" />
                      <span className="hidden sm:inline">
                        Verified by OriginTrail
                      </span>
                      <span className="sm:hidden">Verified</span>
                    </Badge>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-800 text-white">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-white">
                        <CheckCircle className="h-5 w-5 text-emerald-500" />
                        Comment Verification
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                        <p className="text-sm text-emerald-400 font-medium mb-1">
                          This comment has been verified and minted on the
                          OriginTrail Decentralized Knowledge Graph as a
                          Knowledge Asset.
                        </p>
                        <p className="text-xs text-emerald-500/70">
                          Knowledge assets are the atomic unit of the DKG and is
                          utilized to ensure authenticity and immutable record
                        </p>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between py-2 border-b border-zinc-800">
                          <span className="text-sm font-medium text-zinc-400">
                            Network
                          </span>
                          <div className="text-right">
                            <div className="text-sm text-white">
                              {comment.mintInformation.chainName}
                            </div>
                            <div className="text-xs text-zinc-500">
                              {comment.mintInformation.chainFullName}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between py-2 border-b border-zinc-800">
                          <span className="text-sm font-medium text-zinc-400">
                            Token ID
                          </span>
                          <span className="text-sm font-mono text-white">
                            #{comment.mintInformation.token_id}
                          </span>
                        </div>

                        <div className="flex items-center justify-between py-2 border-b border-zinc-800">
                          <span className="text-sm font-medium text-zinc-400">
                            Chain ID
                          </span>
                          <span className="text-sm font-mono text-white">
                            {comment.mintInformation.chainId}
                          </span>
                        </div>

                        <div className="flex items-center justify-between py-2">
                          <span className="text-sm font-medium text-zinc-400">
                            Environment
                          </span>
                          <Badge
                            variant="outline"
                            className={
                              comment.mintInformation.chainType === 'testnet'
                                ? 'border-yellow-500/30 text-yellow-400 bg-yellow-500/10'
                                : 'border-emerald-500/30 text-emerald-400 bg-emerald-500/10'
                            }
                          >
                            {comment.mintInformation.chainType}
                          </Badge>
                        </div>
                      </div>

                      {comment.ual && (
                        <div className="pt-3 border-t border-zinc-800">
                          <span className="text-sm font-medium text-zinc-400 block mb-2">
                            Universal Asset Locator
                          </span>
                          <div className="w-full max-w-full overflow-x-auto">
                            <code className="text-xs bg-zinc-800 p-2 rounded block text-zinc-300 border border-zinc-700 break-all whitespace-pre-wrap w-full max-w-full">
                              {comment.ual}
                            </code>
                          </div>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{formattedDate}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <Heart className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <LinkIcon className="h-4 w-4" />
          </Button>
          {/* Only show dropdown if user is not the creator */}
          {currentUser?.oid !== comment?.creator && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-zinc-900 border-zinc-800 text-white"
              >
                <ReportDialog
                  entityType="comment"
                  entityId={comment._id || comment.id}
                  entityOID={comment.oid}
                  contentPreview={comment.content?.substring(0, 100)}
                  parentDiscussionId={postId}
                  trigger={
                    <DropdownMenuItem
                      onSelect={(e) => e.preventDefault()}
                      className="hover:bg-zinc-800 cursor-pointer"
                    >
                      <Flag className="mr-2 h-4 w-4" />
                      <span>Report Comment</span>
                    </DropdownMenuItem>
                  }
                />
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Content - Same structure as discussion */}
      <div className="prose max-w-none mb-6">
        <p className="text-sm">{comment.content}</p>
      </div>

      {/* Footer - Same as discussion */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-1"
            onClick={handleLike}
            disabled={likeMutation.isPending}
          >
            <ThumbsUp className="h-4 w-4" />
            <span>{comment.likes || 0}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-1"
            onClick={handleDislike}
            disabled={dislikeMutation.isPending}
          >
            <ThumbsDown className="h-4 w-4" />
            <span>{comment.dislikes || 0}</span>
          </Button>
        </div>
        <div className="items-center gap-2 hidden">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowReplyForm(!showReplyForm)}
          >
            <Reply className="h-4 w-4 mr-1" />
            Reply
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Reply Form */}
      {showReplyForm && (
        <div className="mt-4 pt-4 border-t">
          <Textarea
            placeholder="Write a reply..."
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            maxLength={10000}
            className="min-h-[100px]"
          />
          <div className="mt-2 flex items-center justify-between">
            <span
              className={`text-xs ${
                replyContent.length > 10000
                  ? 'text-red-500'
                  : replyContent.length > 9000
                  ? 'text-orange-500'
                  : replyContent.length > 7500
                  ? 'text-yellow-500'
                  : 'text-zinc-500'
              }`}
            >
              {replyContent.length.toLocaleString()} / 10,000
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowReplyForm(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleReplySubmit}
                disabled={!replyContent.trim() || replyContent.length > 10000}
              >
                Post Reply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
